import useActiveGym from "@ui/hooks/active-gym";
import { X } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import * as analytics from "@/lib/analytics";
import { guidanceForced } from "@/lib/guidance-override";
import { cn } from "@/lib/utils";

/**
 * Lightweight in-product guidance: a spotlight walkthrough that steps
 * through elements on the page, and a pulsing hotspot for one-off feature
 * callouts. Any surface can use them by tagging elements with a
 * `data-walkthrough` attribute and describing steps as data.
 *
 * Both persist dismissal per id in localStorage so guidance shows once and
 * never nags. Steps whose target is missing or hidden (a docked panel on a
 * narrow window, for example) are skipped automatically.
 *
 * Every walkthrough and hotspot reports to PostHog by its id, so any
 * guidance built on these components is measurable without extra wiring:
 * "Walkthrough Started" / "Walkthrough Step Viewed" / "Walkthrough
 * Completed" / "Walkthrough Dismissed" (with the step where the user
 * bailed) and "Hotspot Opened" / "Hotspot Dismissed".
 */

export interface WalkthroughStep {
  /** Matches the `data-walkthrough` attribute on the element to spotlight. */
  target: string;
  title: string;
  body: string;
  /** Preferred card side; flips automatically when there is no room. */
  side?: Side;
}

type Side = "top" | "bottom" | "left" | "right";

const STORAGE_KEY = "helios:walkthroughs:v1";
const SPOTLIGHT_PADDING = 8;
const CARD_GAP = 12;
const VIEWPORT_MARGIN = 16;

function readSeen(): Record<string, true> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function walkthroughSeen(id: string): boolean {
  return Boolean(readSeen()[id]);
}

/**
 * `?walkthrough=1` replays every guidance surface on the page for QA.
 * Passing an id, such as `?walkthrough=form-builder`, targets one surface.
 */
export function walkthroughForced(id: string): boolean {
  return guidanceForced("walkthrough", id);
}

export function markWalkthroughSeen(id: string) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readSeen(), [id]: true }));
  } catch {
    // Storage can be unavailable (private browsing); guidance just repeats.
  }
}

/**
 * Opens a walkthrough once per browser, after a short delay so the screen
 * settles first. Closing (finished or skipped) marks it seen; a skipped tour
 * never comes back to nag.
 */
export function useFirstRunWalkthrough(id: string, enabled = true) {
  const [open, setOpen] = useState(false);
  const forced = walkthroughForced(id);

  useEffect(() => {
    if ((!enabled && !forced) || (!forced && walkthroughSeen(id))) return;
    const timeout = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(timeout);
  }, [enabled, forced, id]);

  const close = useCallback(() => {
    setOpen(false);
    markWalkthroughSeen(id);
  }, [id]);

  return { open, close };
}

function findTarget(name: string): HTMLElement | null {
  const el = document.querySelector<HTMLElement>(`[data-walkthrough="${name}"]`);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;
  return el;
}

/** Position the card beside the target, flipping and clamping to fit. */
export function placeCard(
  target: {
    top: number;
    bottom: number;
    left: number;
    right: number;
    width: number;
    height: number;
  },
  side: Side,
  card: { width: number; height: number },
  viewport: { width: number; height: number },
): { top: number; left: number } {
  const fits: Record<Side, boolean> = {
    bottom: target.bottom + CARD_GAP + card.height <= viewport.height - VIEWPORT_MARGIN,
    top: target.top - CARD_GAP - card.height >= VIEWPORT_MARGIN,
    right: target.right + CARD_GAP + card.width <= viewport.width - VIEWPORT_MARGIN,
    left: target.left - CARD_GAP - card.width >= VIEWPORT_MARGIN,
  };
  const order: Side[] = [side, "bottom", "top", "right", "left"];
  const chosen = order.find((candidate) => fits[candidate]) ?? side;

  let top: number;
  let left: number;
  if (chosen === "bottom" || chosen === "top") {
    top = chosen === "bottom" ? target.bottom + CARD_GAP : target.top - CARD_GAP - card.height;
    left = target.left + target.width / 2 - card.width / 2;
  } else {
    left = chosen === "right" ? target.right + CARD_GAP : target.left - CARD_GAP - card.width;
    top = target.top + target.height / 2 - card.height / 2;
  }

  return {
    top: Math.min(Math.max(top, VIEWPORT_MARGIN), viewport.height - card.height - VIEWPORT_MARGIN),
    left: Math.min(Math.max(left, VIEWPORT_MARGIN), viewport.width - card.width - VIEWPORT_MARGIN),
  };
}

/**
 * Spotlight tour: dims the page, highlights one element at a time with a
 * ring that glides between steps, and anchors a card with the step copy and
 * Back / Next controls. Esc or the close button skips the rest.
 *
 * Renders inline (fixed overlays, no portal), so it must mount inside an
 * ancestor without CSS transforms.
 */
export function Walkthrough(props: {
  /** Stable identifier used for seen-tracking callers and analytics. */
  id: string;
  steps: WalkthroughStep[];
  open: boolean;
  onClose: () => void;
}) {
  const [steps, setSteps] = useState<WalkthroughStep[]>([]);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [cardPos, setCardPos] = useState<{ top: number; left: number } | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const { id, open, onClose } = props;
  const { gym } = useActiveGym();
  const gymName = gym?.name;

  // Snapshot the steps whose targets exist when the tour opens, so hidden
  // panels (mobile layouts) drop out instead of pointing at nothing.
  useEffect(() => {
    if (!open) return;
    const visible = props.steps.filter((step) => findTarget(step.target));
    setSteps(visible);
    setIndex(0);
    if (visible.length === 0) onClose();
    else {
      analytics.track("Walkthrough Started", {
        walkthrough: id,
        steps: visible.length,
        gym: gymName,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const step: WalkthroughStep | undefined = steps[index];

  // Step views power drop-off analysis: `step` is 1-based, `target` names
  // the spotlighted element.
  useEffect(() => {
    if (!open || !step) return;
    analytics.track("Walkthrough Step Viewed", {
      walkthrough: id,
      step: index + 1,
      target: step.target,
      steps: steps.length,
      gym: gymName,
    });
  }, [open, id, index, step, steps.length, gymName]);

  // Completed means the user pressed Done on the last step; any other close
  // (X, Esc) is a dismissal recorded with the step where they bailed.
  const complete = useCallback(() => {
    analytics.track("Walkthrough Completed", {
      walkthrough: id,
      steps: steps.length,
      gym: gymName,
    });
    onClose();
  }, [id, steps.length, onClose, gymName]);

  const dismiss = useCallback(() => {
    analytics.track("Walkthrough Dismissed", {
      walkthrough: id,
      step: index + 1,
      target: steps[index]?.target,
      steps: steps.length,
      gym: gymName,
    });
    onClose();
  }, [id, index, steps, onClose, gymName]);

  // Track the target's rectangle while this step is active.
  useLayoutEffect(() => {
    if (!open || !step) return;
    const el = findTarget(step.target);
    if (!el) {
      onClose();
      return;
    }
    el.scrollIntoView({ block: "nearest" });
    const measure = () => setRect(el.getBoundingClientRect());
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, step, onClose]);

  // Position the card once its size is known.
  useLayoutEffect(() => {
    if (!rect || !cardRef.current) return;
    const card = cardRef.current.getBoundingClientRect();
    setCardPos(
      placeCard(
        rect,
        step?.side ?? "bottom",
        { width: card.width, height: card.height },
        {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      ),
    );
  }, [rect, step]);

  const back = useCallback(() => setIndex((current) => Math.max(0, current - 1)), []);
  // Advancing past the last step closes the tour; that side effect stays out
  // of the setState updater so it runs exactly once.
  const next = useCallback(() => {
    if (index >= steps.length - 1) {
      complete();
      return;
    }
    setIndex(index + 1);
  }, [index, steps.length, complete]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") dismiss();
      if (event.key === "ArrowRight") next();
      if (event.key === "ArrowLeft") back();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, next, back, dismiss]);

  if (!open || !step) return null;

  const hole = rect
    ? {
        top: rect.top - SPOTLIGHT_PADDING,
        left: rect.left - SPOTLIGHT_PADDING,
        width: rect.width + SPOTLIGHT_PADDING * 2,
        height: rect.height + SPOTLIGHT_PADDING * 2,
      }
    : null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Shield: blocks interaction with the page while the tour runs. */}
      <div className="absolute inset-0" />
      {hole ? (
        <>
          {/* Dim layer with the spotlight cut out via an even-odd clip-path,
              so the overlay's opacity applies uniformly at any viewport size.
              Dark mode dims harder: a dark scrim over an already-dark page
              needs more strength for the spotlight to stand out. */}
          <div
            className="absolute inset-0 bg-slate-950/70 transition-[clip-path] duration-300 ease-out dark:bg-black/80"
            style={{
              clipPath: `polygon(evenodd, 0 0, 100% 0, 100% 100%, 0 100%, 0 0, ${hole.left}px ${hole.top}px, ${hole.left}px ${hole.top + hole.height}px, ${hole.left + hole.width}px ${hole.top + hole.height}px, ${hole.left + hole.width}px ${hole.top}px, ${hole.left}px ${hole.top}px)`,
            }}
          />
          <div className="absolute transition-all duration-300 ease-out" style={hole}>
            <div className="absolute inset-0 rounded-lg ring-2 ring-primary shadow-[0_0_36px_4px_hsl(var(--primary)/0.55)] dark:ring-brand-accent dark:shadow-[0_0_44px_8px_hsl(var(--brand-accent)/0.6)]" />
          </div>
        </>
      ) : null}
      <div
        key={index}
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label={step.title}
        className="absolute w-96 max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-background p-5 shadow-xl animate-in fade-in-0 zoom-in-95"
        style={cardPos ?? { top: 0, left: 0, visibility: "hidden" }}
      >
        <button
          type="button"
          aria-label="Skip tour"
          onClick={dismiss}
          className="absolute right-3 top-3 rounded-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <X className="size-4" />
        </button>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-primary dark:text-brand-accent">
          Step {index + 1} of {steps.length}
        </p>
        <p className="mt-1 pr-6 text-base font-semibold">{step.title}</p>
        <p className="mt-1 text-sm text-muted-foreground">{step.body}</p>
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {steps.map((dot, dotIndex) => (
              <span
                key={dot.target}
                className={cn(
                  "h-1.5 rounded-full transition-all duration-300",
                  dotIndex === index
                    ? "w-4 bg-primary dark:bg-brand-accent"
                    : "w-1.5 bg-muted-foreground/25",
                )}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {index > 0 ? (
              <Button variant="ghost" size="sm" onClick={back}>
                Back
              </Button>
            ) : null}
            <Button size="sm" onClick={next} autoFocus>
              {index >= steps.length - 1 ? "Done" : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Pulsing dot that calls out one feature in place. Clicking it opens a
 * short explainer; "Got it" dismisses it for good.
 */
export function Hotspot(props: {
  id: string;
  title: string;
  body: string;
  side?: Side;
  className?: string;
}) {
  // Starts hidden so server and first client render match; reveals after
  // the seen check runs on the client.
  const [dismissed, setDismissed] = useState(true);
  const [open, setOpen] = useState(false);
  const { gym } = useActiveGym();
  const gymName = gym?.name;
  const forced = walkthroughForced(props.id);

  useEffect(() => {
    setDismissed(forced ? false : walkthroughSeen(props.id));
  }, [forced, props.id]);

  // Shown fires when the dot actually reveals, so open and dismissal rates
  // have a denominator. At most once per session: hotspots often live in
  // chrome that remounts on every navigation.
  useEffect(() => {
    if (dismissed) return;
    const sessionKey = `helios:hotspot:shown:${props.id}`;
    try {
      if (window.sessionStorage.getItem(sessionKey) === "1") return;
      window.sessionStorage.setItem(sessionKey, "1");
    } catch {
      // Without sessionStorage the event may repeat across page views.
    }
    analytics.track("Hotspot Shown", { hotspot: props.id, gym: gymName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissed, props.id]);

  if (dismissed) return null;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) analytics.track("Hotspot Opened", { hotspot: props.id, gym: gymName });
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={props.title}
          className={cn("relative flex size-4 items-center justify-center", props.className)}
        >
          <span className="absolute size-2.5 animate-ping rounded-full bg-primary/40 dark:bg-brand-accent/40" />
          <span className="relative size-2 rounded-full bg-primary dark:bg-brand-accent" />
        </button>
      </PopoverTrigger>
      <PopoverContent side={props.side ?? "right"} className="w-72 p-4">
        <p className="text-sm font-semibold">{props.title}</p>
        <p className="mt-1 text-sm text-muted-foreground">{props.body}</p>
        <Button
          size="sm"
          variant="outline"
          className="mt-3"
          onClick={() => {
            analytics.track("Hotspot Dismissed", { hotspot: props.id, gym: gymName });
            markWalkthroughSeen(props.id);
            setDismissed(true);
            setOpen(false);
          }}
        >
          Got it
        </Button>
      </PopoverContent>
    </Popover>
  );
}
